import React from 'react'
// libs
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector, reduxForm, FieldArray } from 'redux-form'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// components
import AiSuggestedPrompts from './components/TemplatePrompts'
import ResponseTypesSets from './components/ResponseTypesSets'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput, ReduxSwitch } from 'components/FormElements'
// views
import { Content, Form } from './views'
import { CopyIcon } from 'components/Modals/PropertyDetailsModal/components/Form/view'
import { BottomPaper } from 'themes/views/settings'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// constants
import { Forms } from 'constants/forms'
// utils
import { required } from 'utils/validators'

const selector = formValueSelector(Forms.TEMPLATE_SETTINGS_FORM)

export interface TemplateSettingsFormFields {
  createUrl: string
  isPublicSearchable: boolean
  customerUniqueIdPrefix: string
  generateCustomerUniqueId: boolean
  includeInAllAiContext: boolean
  aiEnabled: boolean
  aiContextDescription: string
  archiveOnDelete: boolean
  aiSuggestedPrompts: string[]
}

interface Props {
  createUrl: string
  hideModal: () => void
  publicCreateUrl: string
}

const TemplateSettingsForm: React.FC<InjectedFormProps<TemplateSettingsFormFields, Props> & Props> = ({
  createUrl,
  hideModal,
  handleSubmit,
  publicCreateUrl
}) => {
  const dispatch = useDispatch()

  const {
    permissionsFromSecurityGroups: { riskArchive }
  } = useCurrentUser()

  const { enqueueSnackbar } = useSnackbar()

  const {
    permissionsFromSecurityGroups: { aiManageSettings }
  } = useCurrentUser()

  const isGuestAccess = useSelector((state) => selector(state, 'isGuestAccess'))
  const customerUniqueIdPrefix = useSelector((state) => selector(state, 'customerUniqueIdPrefix'))
  const generateCustomerUniqueId = useSelector((state) => selector(state, 'generateCustomerUniqueId'))

  const validations = generateCustomerUniqueId ? [required] : []

  const onChange = (_event: React.ChangeEvent<any>, checked: boolean) => {
    if (!checked && customerUniqueIdPrefix) {
      dispatch(change(Forms.TEMPLATE_SETTINGS_FORM, 'customerUniqueIdPrefix', null))
    }
  }

  const onCopyClick = (isPublic: boolean) => () => {
    navigator.clipboard.writeText(isPublic ? publicCreateUrl : createUrl)

    enqueueSnackbar('Copied!', {
      variant: 'info',
      autoHideDuration: 1000
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Content>
        <ReduxSwitch name='isGuestAccess' label='Public' reverse />
        <ReduxSwitch name='isPublicSearchable' label='Public Searchable' reverse />
        <ReduxSwitch name='generateCustomerUniqueId' listenOnChange={onChange} label='Generate Unique ID' reverse />
        {riskArchive && <ReduxSwitch name='archiveOnDelete' label='Enable Archive' reverse />}
        <ReduxInput
          multiline
          label='Prefix'
          validate={validations}
          name='customerUniqueIdPrefix'
          disabled={!generateCustomerUniqueId}
        />
        <ReduxInput
          disabled
          name='createUrl'
          label='Create URL'
          endAdornment={<CopyIcon onClick={onCopyClick(false)} />}
        />
        {isGuestAccess && (
          <ReduxInput
            disabled
            label='Public URL'
            name='publicCreateUrl'
            endAdornment={<CopyIcon onClick={onCopyClick(true)} />}
          />
        )}
        {aiManageSettings && (
          <>
            <ReduxSwitch name='aiEnabled' label='AI Enabled' reverse />
            <ReduxSwitch name='includeInAllAiContext' label='Include in Global AI Context' reverse />
            <ReduxInput name='aiContextDescription' label='AI Context Description' />
            <FieldArray name='aiSuggestedPrompts' component={AiSuggestedPrompts} />
          </>
        )}
        <ResponseTypesSets />
      </Content>
      <BottomPaper square>
        <Button type='submit' layout={BUTTON_LAYOUTS.PURPLE}>
          Save
        </Button>
        <Button layout={BUTTON_LAYOUTS.WHITE} type='button' onClick={hideModal}>
          Cancel
        </Button>
      </BottomPaper>
    </Form>
  )
}

export default reduxForm<TemplateSettingsFormFields, Props>({
  form: Forms.TEMPLATE_SETTINGS_FORM,
  enableReinitialize: true
})(TemplateSettingsForm)
